import { useState } from 'react';
import { X, CheckCircle, XCircle, Clock, ArrowRight, Copy, CaretDown, CaretUp } from '@phosphor-icons/react';

export const AgentRunHistory = ({ agentName, history, onClose }) => {
  if (!history || history.length === 0) {
    return (
      <div className="fixed inset-0 z-50 flex items-start justify-center pt-8 sm:pt-16" data-testid="agent-history-modal">
        <div className="absolute inset-0 bg-black/40 backdrop-blur-[2px]" onClick={onClose} />
        <div className="relative bg-white rounded-2xl shadow-xl w-full max-w-md mx-4 overflow-hidden animate-fade-in">
          <div className="flex items-center justify-between px-5 py-4 border-b border-[#f2f4f6]">
            <h2 className="font-bold text-base text-[#2d3338]">{agentName} — History</h2>
            <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-[#f2f4f6]"><X size={18} className="text-[#596065]" /></button>
          </div>
          <div className="p-8 text-center">
            <Clock size={32} className="text-[#acb3b8] mx-auto mb-3" />
            <p className="text-sm text-[#596065]">No runs yet. Trigger a manual run or wait for the schedule.</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center pt-8 sm:pt-16" data-testid="agent-history-modal">
      <div className="absolute inset-0 bg-black/40 backdrop-blur-[2px]" onClick={onClose} />
      <div className="relative bg-white rounded-2xl shadow-xl w-full max-w-lg mx-4 max-h-[80vh] flex flex-col overflow-hidden animate-fade-in">
        <div className="flex items-center justify-between px-5 py-4 border-b border-[#f2f4f6] flex-shrink-0">
          <div>
            <h2 className="font-bold text-base text-[#2d3338]">{agentName}</h2>
            <p className="text-[10px] text-[#acb3b8]">{history.length} run{history.length !== 1 ? 's' : ''} recorded</p>
          </div>
          <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-[#f2f4f6]"><X size={18} className="text-[#596065]" /></button>
        </div>

        <div className="flex-1 overflow-y-auto divide-y divide-[#f2f4f6]">
          {history.slice().reverse().map((run, ri) => (
            <RunEntry key={run.run_id || ri} run={run} />
          ))}
        </div>
      </div>
    </div>
  );
};

function RunEntry({ run }) {
  const [expanded, setExpanded] = useState(false);
  const [copied, setCopied] = useState(false);
  const ok = run.success;
  const ts = run.completed_at ? new Date(run.completed_at) : null;
  const dur = run.duration_seconds != null ? `${run.duration_seconds.toFixed(1)}s` : '';
  const summary = run.context?.summary || '';

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(summary);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {}
  };

  return (
    <div className="px-5 py-3" data-testid={`run-entry-${run.run_id}`}>
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-2">
          {ok ? (
            <CheckCircle size={16} className="text-[#16a34a]" weight="fill" />
          ) : (
            <XCircle size={16} className="text-[#dc2626]" weight="fill" />
          )}
          <span className="text-xs font-semibold text-[#2d3338]">{ok ? 'Completed' : 'Failed'}</span>
        </div>
        <div className="flex items-center gap-2 text-[10px] text-[#acb3b8]">
          {dur && <span>{dur}</span>}
          {ts && <span>{ts.toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })}</span>}
        </div>
      </div>

      {/* Steps */}
      <div className="flex flex-wrap items-center gap-1">
        {(run.steps || []).map((s, si) => (
          <div key={si} className="flex items-center gap-1">
            {si > 0 && <ArrowRight size={8} className="text-[#d1d5db]" />}
            <span
              className={`text-[9px] px-1.5 py-0.5 rounded font-medium ${
                s.status === 'success'
                  ? 'bg-[#dcfce7] text-[#16a34a]'
                  : s.status === 'failed'
                  ? 'bg-[#fee2e2] text-[#dc2626]'
                  : 'bg-[#f2f4f6] text-[#596065]'
              }`}
            >
              {s.name || s.type}
            </span>
          </div>
        ))}
      </div>

      {/* Summary with expand/copy */}
      {summary && (
        <div className="mt-2 bg-[#fafbfc] rounded-lg overflow-hidden">
          <div
            className={`text-[11px] text-[#596065] p-2.5 whitespace-pre-wrap break-words ${expanded ? 'max-h-[300px] overflow-y-auto' : 'max-h-[4.5rem] overflow-hidden'}`}
          >
            {summary}
          </div>
          <div className="flex items-center justify-between px-2.5 py-1.5 border-t border-[#f0f1f3]">
            <button
              onClick={() => setExpanded(!expanded)}
              className="flex items-center gap-1 text-[10px] font-semibold text-[#005bc1] hover:text-[#004faa] transition-colors"
              data-testid={`expand-run-${run.run_id}`}
            >
              {expanded ? <CaretUp size={10} /> : <CaretDown size={10} />}
              {expanded ? 'Collapse' : 'Show full'}
            </button>
            <button
              onClick={handleCopy}
              className="flex items-center gap-1 text-[10px] font-semibold text-[#596065] hover:text-[#2d3338] transition-colors"
              data-testid={`copy-run-${run.run_id}`}
            >
              <Copy size={10} />
              {copied ? 'Copied!' : 'Copy'}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
